import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Home, RefreshCw, X } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const PROFESSIONS = [
  { slug: "educator", label: "Educator" },
  { slug: "healthcare", label: "Healthcare Professional" },
  { slug: "first-responder", label: "First Responder" },
  { slug: "military", label: "Military Member" },
  { slug: "government", label: "Government Employee" },
  { slug: "non-profit", label: "Non-Profit Employee" },
];

const LOAN_RANGES = ["Under $250K", "$250K – $400K", "$400K – $600K", "$600K+"];

const STEP_TITLES = ["Your Goal", "Your Service", "Your Details"];

export const QualifyModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState("");
  const [profession, setProfession] = useState("");
  const [loanRange, setLoanRange] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (open) {
      setStep(0);
      setGoal("");
      setProfession("");
      setLoanRange("");
      setForm({ name: "", email: "", phone: "" });
      setError("");
      setSubmitting(false);
      setCode("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const next = async () => {
    setError("");
    if (step === 0 && !goal) return setError("Choose whether you're buying or refinancing.");
    if (step === 1) {
      if (!profession) return setError("Select the profession that best describes you.");
      if (!loanRange) return setError("Select an estimated loan amount.");
    }
    if (step === 2) {
      if (!form.name.trim()) return setError("Please enter your full name.");
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return setError("Enter a valid email address.");
      if (form.phone.replace(/\D/g, "").length < 10) return setError("Enter a valid 10-digit phone number.");
      setSubmitting(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            goal,
            profession,
            loanRange,
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
          }),
        });
        if (!res.ok) throw new Error("submit failed");
        const data = await res.json();
        setCode(data.confirmationCode);
        setStep(3);
      } catch (e) {
        setError("Something went wrong sending your request. Please try again, or call Randy directly at 770-851-5403.");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStep(step + 1);
  };

  const chip = (active) =>
    `rounded-full border px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] transition-[background-color,border-color,color,transform] duration-300 hover:-translate-y-0.5 ${
      active
        ? "border-[#DC2626] bg-[#DC2626] text-white shadow-[0_8px_24px_rgba(220,38,38,0.35)]"
        : "border-[#0B162C]/20 bg-white text-[#0B162C] hover:border-[#0B162C]"
    }`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="qualify-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-[#060D1A]/75 p-0 backdrop-blur-md sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-[#FAF8F5] p-7 shadow-2xl sm:rounded-3xl sm:p-9"
          >
            <button
              data-testid="qualify-close-btn"
              onClick={onClose}
              aria-label="Close qualification form"
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-[#0B162C]/5 text-[#0B162C] transition-colors hover:bg-[#0B162C]/10"
            >
              <X size={16} />
            </button>

            {step < 3 ? (
              <>
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#D97706]">
                  Georgia Local Heroes · Step {step + 1} of 3
                </p>
                <h3 className="mt-2 font-display text-2xl font-bold text-[#0B162C]">{STEP_TITLES[step]}</h3>
                <div className="mt-4 flex gap-2">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-[#DC2626]" : "bg-[#0B162C]/10"}`}
                    />
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="mt-7"
                  >
                    {step === 0 && (
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          data-testid="qualify-goal-buying"
                          onClick={() => setGoal("buying")}
                          className={`flex flex-col items-center gap-3 rounded-2xl border-2 p-6 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 ${
                            goal === "buying" ? "border-[#DC2626] bg-[#DC2626]/5" : "border-[#0B162C]/15 bg-white hover:border-[#0B162C]/40"
                          }`}
                        >
                          <Home size={26} className={goal === "buying" ? "text-[#DC2626]" : "text-[#0B162C]"} />
                          <span className="font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#0B162C]">Buying</span>
                        </button>
                        <button
                          data-testid="qualify-goal-refinancing"
                          onClick={() => setGoal("refinancing")}
                          className={`flex flex-col items-center gap-3 rounded-2xl border-2 p-6 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 ${
                            goal === "refinancing"
                              ? "border-[#DC2626] bg-[#DC2626]/5"
                              : "border-[#0B162C]/15 bg-white hover:border-[#0B162C]/40"
                          }`}
                        >
                          <RefreshCw size={26} className={goal === "refinancing" ? "text-[#DC2626]" : "text-[#0B162C]"} />
                          <span className="font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#0B162C]">Refinancing</span>
                        </button>
                      </div>
                    )}

                    {step === 1 && (
                      <div className="space-y-6">
                        <div>
                          <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                            I serve as a…
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {PROFESSIONS.map((p) => (
                              <button
                                key={p.slug}
                                data-testid={`profession-chip-${p.slug}`}
                                onClick={() => setProfession(p.slug)}
                                className={chip(profession === p.slug)}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                            Estimated loan amount
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {LOAN_RANGES.map((r, i) => (
                              <button
                                key={r}
                                data-testid={`loan-range-${i}`}
                                onClick={() => setLoanRange(r)}
                                className={chip(loanRange === r)}
                              >
                                {r}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-4">
                        <input
                          data-testid="qualify-name-input"
                          type="text"
                          placeholder="Full name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full rounded-xl border border-[#0B162C]/20 bg-white px-5 py-3.5 text-sm text-[#0B162C] outline-none transition-colors placeholder:text-slate-400 focus:border-[#DC2626]"
                        />
                        <input
                          data-testid="qualify-email-input"
                          type="email"
                          placeholder="Email address"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full rounded-xl border border-[#0B162C]/20 bg-white px-5 py-3.5 text-sm text-[#0B162C] outline-none transition-colors placeholder:text-slate-400 focus:border-[#DC2626]"
                        />
                        <input
                          data-testid="qualify-phone-input"
                          type="tel"
                          placeholder="Phone number"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="w-full rounded-xl border border-[#0B162C]/20 bg-white px-5 py-3.5 text-sm text-[#0B162C] outline-none transition-colors placeholder:text-slate-400 focus:border-[#DC2626]"
                        />
                        <p className="text-[11px] leading-relaxed text-slate-400">
                          Randy Wilson (NMLS #2685815) will reach out personally to confirm your eligibility.
                        </p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {error && (
                  <p data-testid="qualify-error" className="mt-5 text-xs font-semibold text-[#DC2626]">
                    {error}
                  </p>
                )}

                <div className="mt-7 flex items-center justify-between gap-4">
                  <button
                    data-testid="qualify-back-btn"
                    onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
                    className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-[#0B162C]"
                  >
                    <ArrowLeft size={14} /> {step === 0 ? "Cancel" : "Back"}
                  </button>
                  <button
                    data-testid="qualify-next-btn"
                    onClick={next}
                    disabled={submitting}
                    className="group flex items-center gap-2 rounded-full bg-[#DC2626] px-7 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-[background-color,box-shadow] duration-300 hover:bg-[#B91C1C] hover:shadow-[0_10px_30px_rgba(220,38,38,0.4)] disabled:opacity-60"
                  >
                    {submitting ? "Checking…" : step === 2 ? "Submit" : "Continue"}
                    {!submitting && <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />}
                  </button>
                </div>
              </>
            ) : (
              <motion.div
                data-testid="qualify-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="py-6 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
                  className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#DC2626] shadow-[0_16px_50px_rgba(220,38,38,0.45)] ring-4 ring-[#F59E0B]/40"
                >
                  <Check size={34} className="text-white" />
                </motion.div>
                <h3 className="mt-7 font-display text-2xl font-bold text-[#0B162C]">You're on your way, hero.</h3>
                <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-slate-600">
                  Your qualification request is in. Randy Wilson will contact you shortly at the details you provided.
                </p>
                <div className="mx-auto mt-6 inline-block rounded-xl border-2 border-dashed border-[#F59E0B] bg-[#FEF3C7]/50 px-6 py-3">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#D97706]">Confirmation Code</p>
                  <p data-testid="confirmation-code" className="mt-1 font-mono text-lg font-bold tracking-[0.15em] text-[#0B162C]">
                    {code}
                  </p>
                </div>
                <div className="mt-7">
                  <button
                    data-testid="qualify-done-btn"
                    onClick={onClose}
                    className="rounded-full bg-[#0B162C] px-8 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#FAF8F5] transition-colors hover:bg-[#132342]"
                  >
                    Done
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
